/**
 * Archivio API client — F-006 Phase 5 (IMPL-P5-002).
 *
 * Typed `fetch` wrappers for the 3 archivio endpoints:
 *  - GET  /api/v1/archivio                  → ArchivioListResponse
 *  - GET  /api/v1/archivio/:id              → PraticaDetailView
 *  - POST /api/v1/archivio/:id/download     → PresignedUrlResponse
 *
 * Conventions mirror `apps/web/src/features/profilo/lib/profilo-client.ts`:
 *  - `credentials: 'include'` so the Better Auth session cookie travels with
 *    every request.
 *  - Errors thrown by these helpers carry `status` + `code` fields, lifted
 *    from the API JSON envelope (`{ code, message }`).
 *  - Inputs are typed via the shared Zod-inferred types from `@app/shared`.
 *
 * Download endpoint uses POST (not GET) so the presigned URL is never cached
 * by browsers/proxies nor bookmarked by users.
 */
import type {
  ArchivioListQuery,
  ArchivioListResponse,
  PraticaDetailView,
  DownloadRequest,
  PresignedUrlResponse,
} from '@app/shared';

const API = '/api/v1/archivio';

export class ArchivioApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ArchivioApiError';
    this.status = status;
    this.code = code;
  }
}

interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}

async function parseErrorEnvelope(res: Response): Promise<ApiErrorEnvelope> {
  return (await res
    .json()
    .catch(() => ({ code: 'UNKNOWN', message: 'Errore sconosciuto' }))) as ApiErrorEnvelope;
}

/**
 * GET /api/v1/archivio — search + paginated list.
 *
 * Serializes the typed `ArchivioListQuery` into URL search params (skipping
 * undefined/null/empty values to keep the query string clean and stable as
 * a TanStack Query cache key).
 */
export async function searchPratiche(
  query: Partial<ArchivioListQuery>,
): Promise<ArchivioListResponse> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.length === 0) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  const res = await fetch(`${API}${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!res.ok) {
    const env = await parseErrorEnvelope(res);
    throw new ArchivioApiError(
      res.status,
      env.code ?? 'LIST_FAILED',
      env.message ?? "Impossibile caricare l'archivio",
    );
  }
  return (await res.json()) as ArchivioListResponse;
}

/**
 * GET /api/v1/archivio/:id — detail view (anagrafica + documenti + cronologia).
 */
export async function getPraticaDetail(id: string): Promise<PraticaDetailView> {
  const res = await fetch(`${API}/${encodeURIComponent(id)}`, { credentials: 'include' });
  if (!res.ok) {
    const env = await parseErrorEnvelope(res);
    throw new ArchivioApiError(
      res.status,
      env.code ?? 'DETAIL_FAILED',
      env.message ?? 'Impossibile caricare la pratica',
    );
  }
  return (await res.json()) as PraticaDetailView;
}

/**
 * POST /api/v1/archivio/:id/download — request a presigned URL.
 *
 * SECURITY: POST (not GET) prevents browser/proxy caching of the short-lived
 * presigned URL and removes the ability to bookmark or share it. The server
 * accepts ONLY `{ fileType }` (`.strict()`); the S3 key is computed server-side
 * from the enum to prevent path traversal/enumeration from the client.
 */
export async function downloadDocument(
  praticaId: string,
  req: DownloadRequest,
): Promise<PresignedUrlResponse> {
  const res = await fetch(`${API}/${encodeURIComponent(praticaId)}/download`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const env = await parseErrorEnvelope(res);
    throw new ArchivioApiError(
      res.status,
      env.code ?? 'DOWNLOAD_FAILED',
      env.message ?? 'Impossibile preparare il download',
    );
  }
  return (await res.json()) as PresignedUrlResponse;
}

export function isArchivioApiError(err: unknown): err is ArchivioApiError {
  return err instanceof ArchivioApiError;
}
